import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {ClientConfiguration} from "../configuration/client.config";
import {
    IUserModel,
    IUserModelResponse,
    IStoryState,
    IUserModelDelta,
    QuestionState,
    QuestionStateMap,
    CompletionState, RecordReadingStateMap, WordWallMode
} from "./usermodel.objects";
import {EventBus} from "../../event/EventBus";
import {
    UserModelInitializedEvent,
    UserModelUpdateRequestEvent,
    UserModelChangedEvent,
    UserModelUpdateReceivedEvent
} from "../../event/UserModelEvents";
import {SubscriptionBase} from "../../base/SubscriptionBase";
import {RaEvent} from "../../event/RaEvent";
import {Login} from "../../login/login";
import {filter, map} from "rxjs/operators";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {UserModelState} from "./usermodel.state";
import {BookSessionType} from "../../constants";
import { IOptions } from "../http/http";

@Injectable()
export class UserModelService extends SubscriptionBase {

    private _mockProgression: number = 0;

    constructor(private http: HttpClient,
                private config: ClientConfiguration,
                private login:Login,
                private log:BugsnagLoggingService,
                private eventBus:EventBus,
                private userModelState:UserModelState) {

        super("UserModelService");

        let sub = this.eventBus.pipe(filter((e: RaEvent) => e.type === UserModelUpdateRequestEvent.TYPE))
            .subscribe((e: UserModelUpdateRequestEvent) => {
                if (!!this.userModelState.storyUniqName) {
                    this.retrieveUserModel(this.userModelState.storyUniqName);
                }
            });
        this.subscriptions.push(sub);

        sub = this.eventBus.pipe(filter((e: RaEvent) => e.type === UserModelUpdateReceivedEvent.TYPE))
            .subscribe((e: UserModelUpdateReceivedEvent) => {
               this.updateUserModel(e.userModel, false);
            });
        this.subscriptions.push(sub);
    }

    private getOptions(): IOptions {
        return ({
            withCredentials: true,
            headers: new HttpHeaders({ "X-Forwarded-Host": this.config.slcReferrerDomain })
        });
    }

    public retrieveUserModel(selectionName: string): Observable<IUserModelDelta> {

        let url: string = this.generateUrl(selectionName);

        // TODO:This is just a mock story progression. Only canned files up to usermodel8.json exist
        if (this._mockProgression < 8) {
            this._mockProgression++;
        }

        return this.http.get(url, this.getOptions()).pipe(map(data => {
            this.log.log("Retrieved UserModel");
            let response: IUserModelResponse = data as IUserModelResponse;
            let model: IUserModel = response.UserModel;
            console.log("Received user model ", model);
            return this.updateUserModel(model, true);
        }));
    }

    private generateUrl(selection: string): string {
        let url: string;
        if (this.config.isSuperUserMode) {
            url = "resources/superuser/fullflow/usermodelSuperUser.json";
        } else if (this.config.allowBrowserVersion) {
            url = "resources/superuser/fullflow/usermodel" + this._mockProgression + ".json";
        }
        else {
            url = this.config.userModelPath + "?tenantId=" + this.login.tenantId + "&learnerId=" +
                this.login.learnerId + "&httpSessionId=" + this.login.sessionId + "&storyUniqName=" + selection;
        }
        return url;
    }

    public updateUserModel(model: IUserModel, initialize: boolean): IUserModelDelta {
        this.log.log("User Model Update received");

        let delta: IUserModelDelta = this.userModelState.updateUserModelState(model, initialize);

        this.log.log("Sending User Model Changed Event");
        if (initialize) {
            this.eventBus.next(new UserModelInitializedEvent(delta));
        } else {
            this.eventBus.next(new UserModelChangedEvent(delta));
        }
        return delta;
    }

    public findThinkAboutQuestionState(id: number): QuestionState {
        return this.userModelState.findQuizQuestionState(id);
    }

    public findQuizQuestionState(id: number): QuestionState {
        return this.userModelState.findQuizQuestionState(id);
    }

    public get learnerName(): string {
        return this.userModelState.learnerName;
    }

    public currentStep(): BookSessionType {
        return this.userModelState.currentStep();
    }

    public get currentSentenceId(): number {
        return this.userModelState.currentSentenceId;
    }

    public get currentPageId(): number {
        return this.userModelState.currentPageId;
    }

    public get currentQuizQuestionState(): QuestionState {
        return this.userModelState.currentQuizQuestionState;
    }

    public get currentQuizQuestionId(): number {
        return this.userModelState.currentQuizQuestionId;
    }

    // number of times user has recorded the story
    public get recordCount(): number {
        return this.userModelState.recordCount;
    }

    public get quizCount(): number {
        return this.userModelState.quizCount;
    }

    public get highestWcpm(): number {
        return this.userModelState.highestWcpm;
    }

    public get lastQuizId(): number {
        return this.userModelState.lastQuizId;
    }

    public get recordingBadge(): number {
        return this.userModelState.recordingBadge;
    }

    public get recordingPoint(): number {
        return this.userModelState.recordingPoint;
    }

    public get canPlayback(): boolean {
        return this.userModelState.canPlayback;
    }

    public get thinkAboutQuestionPoint(): number {
        return this.userModelState.thinkAboutQuestionPoint;
    }

    public get thinkAboutQuestionStates(): {id: string, status: QuestionState}[] {
        return this.userModelState.thinkAboutQuestionStates;
    }

    public get quizQuestionStates(): {id: string, status: QuestionState}[] {
        return this.userModelState.quizQuestionStates;
    }

    public get thinkAboutQuestionStatesMap(): {} {
        return this.userModelState.thinkAboutQuestionStatesMap;
    }

    public get quizPoint(): number {
        return this.userModelState.quizPoint;
    }

    public get storyTotalPoint(): number {
        return this.userModelState.storyTotalPoint;
    }

    public get storyCompletionState(): CompletionState {
        return this.userModelState.storyCompletionState;
    }

    public get recordReadingStates(): RecordReadingStateMap {
        return this.userModelState.recordReadingStates;
    }

    public get hasTAQStar(): boolean {
        return this.userModelState.hasTAQStar;
    }

    public get hasRecordingStar(): boolean {
        return this.userModelState.hasRecordingStar;
    }

    // quiz score (good/ok)
    public get hasQuizStar(): boolean {
        return this.userModelState.hasQuizStar;
    }

    public get storyUniqName(): string {
        return this.userModelState.storyUniqName;
    }

    public get totalPoint(): number {
        return this.userModelState.totalPoint;
    }

    public get wordWallMode(): WordWallMode {
        return this.userModelState.wordWallMode;
    }

    public get taqCompletionState(): CompletionState {
        return this.userModelState.taqCompletionState;
    }

    public get quizCompletionState(): CompletionState {
        return this.userModelState.quizCompletionState;
    }

    public get recordCompletionState(): CompletionState {
        return this.userModelState.recordCompletionState;
    }
}
